import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/services/api';
import {
  Inbox as InboxIcon,
  Bell,
  CheckCheck,
  User,
  MessageSquare,
  ArrowRight,
  Circle,
  CheckCircle2,
} from 'lucide-react';
import clsx from 'clsx';

export function Inbox() {
  const { workspaceId, projectId } = useParams<{ workspaceId: string; projectId: string }>();
  const api = useApi();
  const queryClient = useQueryClient();

  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  const { data: notifications, isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.getNotifications('member-1'),
  });

  const { data: workItems } = useQuery({
    queryKey: ['workItems', projectId],
    queryFn: () => api.getWorkItems(projectId!),
    enabled: !!projectId,
  });

  const { data: members } = useQuery({
    queryKey: ['members', workspaceId],
    queryFn: () => api.getMembers(workspaceId!),
    enabled: !!workspaceId,
  });

  const markReadMutation = useMutation({
    mutationFn: (id: string) => api.markNotificationRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => api.markAllNotificationsRead('member-1'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const getMemberName = (memberId: string) =>
    members?.find((m: any) => m.bh_memberid === memberId)?.bh_name || 'Someone';

  const getWorkItemName = (workItemId: string) =>
    workItems?.find((wi: any) => wi.bh_workitemid === workItemId)?.bh_name || 'an issue';

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'mention':
        return <MessageSquare className="w-4 h-4 text-blue-500" />;
      case 'assigned':
        return <User className="w-4 h-4 text-green-500" />;
      case 'state_change':
        return <ArrowRight className="w-4 h-4 text-orange-500" />;
      case 'comment':
        return <MessageSquare className="w-4 h-4 text-purple-500" />;
      default:
        return <Bell className="w-4 h-4 text-gray-500" />;
    }
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const filteredNotifications = notifications?.filter((n: any) =>
    filter === 'all' ? true : !n.bh_read
  ) || [];

  const unreadCount = notifications?.filter((n: any) => !n.bh_read).length || 0;

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      <div className="max-w-3xl mx-auto py-6 px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <InboxIcon className="w-8 h-8 text-primary-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Inbox</h1>
              <p className="text-sm text-gray-500">
                {unreadCount > 0 ? `${unreadCount} unread notifications` : 'All caught up!'}
              </p>
            </div>
          </div>
          {unreadCount > 0 && (
            <button
              onClick={() => markAllReadMutation.mutate()}
              className="flex items-center gap-2 px-4 py-2 text-sm text-primary-600 hover:bg-primary-50 rounded-lg"
            >
              <CheckCheck className="w-4 h-4" />
              Mark all as read
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setFilter('all')}
            className={clsx(
              'px-4 py-2 text-sm font-medium rounded-lg transition-colors',
              filter === 'all'
                ? 'bg-primary-100 text-primary-700'
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
            )}
          >
            All
          </button>
          <button
            onClick={() => setFilter('unread')}
            className={clsx(
              'px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2',
              filter === 'unread'
                ? 'bg-primary-100 text-primary-700'
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
            )}
          >
            Unread
            {unreadCount > 0 && (
              <span className="px-1.5 py-0.5 bg-primary-600 text-white text-xs rounded-full">
                {unreadCount}
              </span>
            )}
          </button>
        </div>

        {/* Notifications List */}
        {filteredNotifications.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <InboxIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
            </h3>
            <p className="text-gray-500">
              {filter === 'unread'
                ? "You're all caught up!"
                : 'When someone mentions you or assigns you to an issue, you\'ll see it here.'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 divide-y divide-gray-100">
            {filteredNotifications.map((notification: any) => (
              <div
                key={notification.bh_notificationid}
                className={clsx(
                  'p-4 flex gap-4 hover:bg-gray-50 transition-colors cursor-pointer',
                  !notification.bh_read && 'bg-primary-50/30'
                )}
                onClick={() => {
                  if (!notification.bh_read) {
                    markReadMutation.mutate(notification.bh_notificationid);
                  }
                }}
              >
                {/* Read/Unread indicator */}
                <div className="pt-1">
                  {notification.bh_read ? (
                    <CheckCircle2 className="w-5 h-5 text-gray-300" />
                  ) : (
                    <Circle className="w-5 h-5 text-primary-500 fill-primary-500" />
                  )}
                </div>

                {/* Icon */}
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                  {getNotificationIcon(notification.bh_type)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900">
                    <span className="font-medium">{getMemberName(notification.bh_actor)}</span>
                    {' '}
                    <span className="text-gray-600">{notification.bh_message}</span>
                  </p>
                  <p className="text-sm text-primary-600 mt-1 truncate">
                    {getWorkItemName(notification.bh_workitem)}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {formatTime(notification.createdon)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Activity Feed */}
        <div className="mt-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="space-y-4">
              {workItems?.slice(0, 5).map((wi: any) => (
                <div key={wi.bh_workitemid} className="flex items-start gap-3 text-sm">
                  <div className="w-2 h-2 rounded-full bg-primary-500 mt-2" />
                  <div>
                    <p className="text-gray-900">
                      <span className="font-medium">#{wi.bh_sequenceid}</span> {wi.bh_name}
                    </p>
                    <p className="text-xs text-gray-500">
                      Created {new Date(wi.createdon).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
